/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, DoCheck, Inject, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './team-analysis-page.component.html';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {lineItemListCommons} from '../../line-item/line-item-commons';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {OrderResource} from '../../../components/api/resources/resource/order.resource';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {LineItemResource} from '../../../components/api/resources/resource/line-item.resource';
import {RoleResource} from '../../../components/api/resources/resource/role.resource';
import {UserResource} from '../../../components/api/resources/resource/user.resource';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {createBufferedFlag, readBufferedFlag} from '../../common/util/template-state';


type TeamAnalysisTab = 'members' | 'lineItems';

@Component({
  standalone: false,
  selector: 'adn-team-analysis-page',
  template
})
export class TeamAnalysisPageComponent implements OnInit, DoCheck, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  /** Route isNew never flips after save; modelComms does. Bind templates to this. */
  effectiveIsNew = false;

  activeTab: TeamAnalysisTab = 'members';

  roles: any[] = [];
  users: any[] = [];
  otherUsers: any[] = [];
  teamMemberRoleOptions: any[] = [];
  hasNewUser = false;
  loaded = false;
  /**
   * Cached Role/User queries can settle during the first members-tab CD.
   * [resolved] / *ngIf="loaded" would then flip false→true mid-check (NG0100).
   */
  private readonly loadedFlag = createBufferedFlag();
  /** Seed badge text — bare {} left totalCountText undefined→count (NG0100). */
  lineItems: any = {totalCountText: ''};
  seesLinks = false;

  private readonly networkId: string;
  private lastLoadedModelId: string | undefined;
  private modelCommsUnsub: (() => void) | null = null;
  private destroyed = false;
  private subscribedModelComms: any;

  constructor(
    @Inject(RoleResource) private readonly Role: any,
    @Inject(UserResource) private readonly User: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(OrderResource) private readonly Order: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly translate: TranslateService,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.networkId = LocalNetworkProfile.getNetworkId();
    this.seesLinks = this.LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM');
  }

  ngOnInit(): void {
    this.effectiveIsNew = !!this.isNew;
    this.subscribeModelComms();
    if (this.effectiveIsNew) {
      this.loaded = true;
      return;
    }
    if (this.model?.id) {
      this.lastLoadedModelId = this.model.id;
      this.refreshUsers();
      this.setupLineItems();
    }
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId === this.lastLoadedModelId || this.effectiveIsNew) {
      return;
    }
    this.lastLoadedModelId = modelId;
    // Macrotask — starting list/user loads mid-CD lets totalCountText land in the
    // same verify pass as the first badge bind (undefined/'')→count NG0100.
    setTimeout(() => {
      if (this.destroyed || this.lastLoadedModelId !== modelId) {
        return;
      }
      this.ngZone.run(() => {
        this.refreshUsers();
        this.setupLineItems();
      });
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.modelCommsUnsub) {
      this.modelCommsUnsub();
    }
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  trackByUserId(_index: number, user: any): string {
    return user.id;
  }

  trackByOtherUserId(_index: number, user: any): string {
    return user.id;
  }

  selectTab(tab: TeamAnalysisTab): void {
    this.activeTab = tab;
  }

  addUser(): void {
    if (!this.otherUsers.length || !this.teamMemberRoleOptions.length) {
      return;
    }
    this.hasNewUser = true;
    const defaultUser = this.otherUsers[0];
    this.users.unshift({
      id: defaultUser.id,
      username: defaultUser.username,
      displayName: defaultUser.displayName,
      objectState: defaultUser.objectState,
      obfuscated: defaultUser.obfuscated,
      newUser: true,
      active: true,
      edited: true,
      teamRole: this.teamMemberRoleOptions[0]
    });
  }

  newUserChange(user: any): void {
    const otherUser = _.find(this.otherUsers, ['id', user.id]);
    if (!otherUser) {
      return;
    }
    user.username = otherUser.username;
    user.displayName = otherUser.displayName;
    user.objectState = otherUser.objectState;
    user.obfuscated = otherUser.obfuscated;
  }

  editUser(user: any): void {
    user.edited = true;
    user.active = true;
    user.prevRole = user.teamRole;
  }

  cancelUser(user: any, index: number): void {
    user.edited = false;
    user.teamRole = user.prevRole;
    user.failSave = false;
    if (user.newUser) {
      _.pullAt(this.users, index);
      this.hasNewUser = false;
    }
  }

  saveUser(user: any): void {
    this.User.get({id: user.id}).$promise.then((apiUser: any) => {
      const networkRoles = _.find(apiUser.userRoles.rolesByNetwork, ['network.tagId', this.networkId]);
      if (networkRoles && networkRoles.roles) {
        const team = _.find(networkRoles.roles, ['team.id', this.model.id]);
        if (user.active) {
          const roleUpdate = {id: user.teamRole.id};
          if (team) {
            team.role = roleUpdate;
          } else {
            networkRoles.roles.push({team: {id: this.model.id}, role: roleUpdate});
          }
        } else {
          const indexEntryRemove = _.findIndex(networkRoles.roles, ['team.id', this.model.id]);
          _.pullAt(networkRoles.roles, indexEntryRemove);
        }
      }
      return apiUser.savePartial('userRoles');
    }).then(() => {
      this.ngZone.run(() => {
        user.edited = false;
        user.failSave = false;
        if (user.newUser) {
          user.newUser = false;
          _.remove(this.otherUsers, (otherUser: any) => otherUser.id === user.id);
        } else if (!user.active) {
          _.remove(this.users, (item: any) => item.id === user.id);
          this.otherUsers.push(user);
        }
        this.hasNewUser = false;
      });
    }).catch(() => {
      this.ngZone.run(() => {
        user.failSave = true;
      });
    });
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelCommsUnsub = this.modelComms.addSubscriber((data: any, type: any, isNewFromComms?: boolean) => {
      if (type) {
        return;
      }
      const wasNew = this.effectiveIsNew;
      if (isNewFromComms === true || isNewFromComms === false) {
        this.effectiveIsNew = isNewFromComms;
      } else if (data?.name) {
        this.effectiveIsNew = false;
      }
      if (wasNew && !this.effectiveIsNew && this.model?.id) {
        this.lastLoadedModelId = this.model.id;
        this.refreshUsers();
        this.setupLineItems();
      } else if (!this.effectiveIsNew && !this.otherUsers.length) {
        this.refreshUsers();
      }
      this.cdr.markForCheck();
    });
  }

  private setupLineItems(): void {
    if (!this.model?.id) {
      return;
    }
    // Keep a stable badge string across list helper re-init.
    this.lineItems = {totalCountText: this.lineItems?.totalCountText || ''};
    this.adnListHelper.setUpBasicList(this.lineItems, this.LineItem, '', {
      moreParams: {team: this.model.id},
      baseTable: 'lineItem',
      afterFill: (afterFill: boolean) => {
        this.lineItems.perms = [];
        lineItemListCommons(
          this.lineItems,
          afterFill && this.lineItems.allFields,
          this.LocalNetworkProfile,
          this.adnTimeShifter,
          this.translate,
          this.appLocale,
          this.LocalUserPermissions,
          this.Team,
          this.Order,
          'SLIM'
        );
      }
    });
  }

  private refreshUsers(): void {
    if (!this.model?.id) {
      this.roles = [];
      this.users = [];
      this.otherUsers = [];
      this.teamMemberRoleOptions = [];
      this.hasNewUser = false;
      this.loaded = true;
      return;
    }

    this.loaded = false;
    this.Role.query().$promise.then((rolePage: any) => {
      return this.User.query({
        concise: true,
        pageSize: LocalNetworkInfo.obtain().getInfo('userCount') + 50
      }).$promise.then((userPage: any) => {
        return {roles: rolePage.results, allUsers: userPage.results};
      });
    }).then(({roles, allUsers}) => {
      this.ngZone.run(() => {
        this.roles = roles;
        this.refreshTeamMemberRoleOptions();
        this.hasNewUser = false;
        _.forEach(allUsers, (user: any) => {
          user.obfuscated = user.username.indexOf('@') < 0 && user.username.indexOf('*') > 0;
          user.teamRole = null;
        });

        const selection = (user: any) => {
          const rolesByNetwork = _.find(user.userRoles.rolesByNetwork, (x: any) => {
            return x.network.id === this.networkId || x.network.tagId === this.networkId;
          });
          if (!rolesByNetwork) {
            return false;
          }
          const teamRole = _.find(rolesByNetwork.roles, ['team.id', this.model.id]);
          if (teamRole) {
            user.teamRole = _.find(this.roles, ['id', teamRole.role.id]);
          }
          return user.teamRole;
        };

        this.users = _.filter(allUsers, selection);
        this.otherUsers = _.reject(allUsers, selection);
        this.loaded = true;
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.roles = [];
        this.users = [];
        this.otherUsers = [];
        this.teamMemberRoleOptions = [];
        this.hasNewUser = false;
        this.loaded = true;
      });
    });
  }

  private refreshTeamMemberRoleOptions(): void {
    const politicalTypes = ['CONTROLLER', 'SPONSOR'];
    this.teamMemberRoleOptions = _.filter(this.roles, (r: any) => {
      if (r.scope !== 'TEAM') {
        return false;
      }
      if (politicalTypes.indexOf(this.model.type) > -1) {
        return r.application === 'POLITICAL';
      }
      return r.application !== 'POLITICAL';
    });
  }
}
